"use client";

import { useEffect, useRef, useState } from "react";
import { Link, usePathname } from "@/i18n/navigation";

interface Item {
    href: string;
    label: string;
    external?: boolean;
}

const triggerCls =
    "flex-shrink-0 inline-flex items-center gap-0.5 whitespace-nowrap rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-accent hover:text-accent-foreground sm:px-3 sm:text-sm";

const itemCls =
    "block whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground";

function isActive(pathname: string, href: string): boolean {
    if (href === "/") return pathname === "/";
    return pathname === href || pathname.startsWith(href + "/");
}

export default function MoreMenu({ items, label }: { items: Item[]; label: string }) {
    const [open, setOpen] = useState(false);
    const pathname = usePathname();
    const ref = useRef<HTMLDivElement>(null);
    const anyActive = items.some((i) => !i.external && isActive(pathname, i.href));

    useEffect(() => {
        if (!open) return;
        const onPointer = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onPointer);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onPointer);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const [prevPath, setPrevPath] = useState(pathname);
    if (prevPath !== pathname) {
        setPrevPath(pathname);
        if (open) setOpen(false);
    }

    const triggerColor = anyActive
        ? "bg-accent text-accent-foreground"
        : "text-muted-foreground";

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-haspopup="menu"
                className={`${triggerCls} ${triggerColor}`}
            >
                {label}
                <span aria-hidden className="text-[0.7em] leading-none">▾</span>
            </button>
            {open && (
                <div
                    role="menu"
                    className="more-menu-panel absolute right-0 top-full mt-1 min-w-[8rem] origin-top-right rounded-md border border-border bg-popover p-1 shadow-md"
                >
                    {items.map((item) => {
                        const active = !item.external && isActive(pathname, item.href);
                        const cls = `${itemCls} ${
                            active
                                ? "bg-accent text-accent-foreground"
                                : "text-muted-foreground"
                        }`;
                        if (item.external) {
                            return (
                                <a
                                    key={item.href}
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cls}
                                    role="menuitem"
                                >
                                    {item.label}
                                </a>
                            );
                        }
                        return (
                            <Link
                                key={item.href}
                                href={item.href}
                                className={cls}
                                role="menuitem"
                                aria-current={active ? "page" : undefined}
                            >
                                {item.label}
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
